import { BEYOND_WORDS_PROJECT_ID } from "@helper/getEnvVariables";
import { BeyondWordsPlayerInstance } from "@typings/BeyondWords";

export const registerAnalyticsEvents = (
  player: BeyondWordsPlayerInstance,
  useAudioAnalytics: ReturnType<
    (typeof import("@hooks/useAnalyticsCall/useAudioAnalytics"))["useAudioAnalytics"]
  >,
  getTitle: () => string,
  getArticleId: () => string,
  onClosedByUser?: () => void,
) => {
  const getAudioId = () => `${BEYOND_WORDS_PROJECT_ID}::${getArticleId()}`;

  let audioListenedTime = 0;
  let audioDuration = 0;
  let audioListenedpercentage = 0;
  let audioSpeed = 1;

  const calculateAudioPercentage = (listenedTime: number, duration: number) => {
    return duration === 0 ? 0 : Math.floor((listenedTime / duration) * 100);
  };

  player.addEventListener("VisibilityChanged", () => {
    // user clicked close button
    if (player.widgetStyle === "closed-by-user") {
      audioListenedTime = player.currentTime || 0;

      audioListenedpercentage = calculateAudioPercentage(
        audioListenedTime,
        audioDuration,
      );

      useAudioAnalytics(
        "audio_close",
        getTitle(),
        getAudioId(),
        audioListenedTime,
        audioDuration,
        audioListenedpercentage,
        audioSpeed,
      );

      onClosedByUser?.();

      return;
    }
  });

  let audioEvent = "audio_start";
  player.addEventListener("PressedPlay", () => {
    audioDuration = player.duration || 0;
    audioListenedTime = player.currentTime || 0;
    audioSpeed = player.playbackRate || 1;
    audioListenedpercentage = calculateAudioPercentage(
      audioListenedTime,
      audioDuration,
    );

    useAudioAnalytics(
      audioEvent,
      getTitle(),
      getAudioId(),
      audioListenedTime,
      audioDuration,
      audioListenedpercentage,
      audioSpeed,
    );

    audioEvent = audioEvent === "audio_start" ? "audio_resume" : audioEvent;
  });

  player.addEventListener("PressedPause", () => {
    audioListenedTime = player.currentTime || 0;

    useAudioAnalytics(
      "audio_pause",
      getTitle(),
      getAudioId(),
      audioListenedTime,
      audioDuration,
      calculateAudioPercentage(audioListenedTime, audioDuration),
      audioSpeed,
    );
  });

  player.addEventListener("PressedPrevSegment", () => {
    audioListenedTime = player.currentTime || 0;

    useAudioAnalytics(
      "audio_rewind",
      getTitle(),
      getAudioId(),
      audioListenedTime,
      audioDuration,
      calculateAudioPercentage(audioListenedTime, audioDuration),
      audioSpeed,
    );
  });

  player.addEventListener("PressedNextSegment", () => {
    audioListenedTime = player.currentTime || 0;

    useAudioAnalytics(
      "audio_forward",
      getTitle(),
      getAudioId(),
      audioListenedTime,
      audioDuration,
      calculateAudioPercentage(audioListenedTime, audioDuration),
      audioSpeed,
    );
  });

  player.addEventListener("PressedChangeRate", () => {
    audioListenedTime = player.currentTime || 0;
    audioSpeed = player.playbackRate || 1;

    useAudioAnalytics(
      "audio_click_speed",
      getTitle(),
      getAudioId(),
      audioListenedTime,
      audioDuration,
      calculateAudioPercentage(audioListenedTime, audioDuration),
      audioSpeed,
    );
  });
};
